import React from 'react'
import PropTypes from 'prop-types'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'

import useColumnConfig from '../hooks/useColumnConfig'

/**
 * Prompts grid. Row dragging is only enabled when `onRowOrderChange` is supplied — the API
 * returns search results in relevance order, so a drop cannot be persisted while searching.
 * @param {object} props - component props
 * @param {Array<object>} props.prompts - prompts to render, in display order
 * @param {boolean} props.isLoading - whether the list query is still resolving
 * @param {Function} props.onDelete - called with a prompt id when its delete action fires
 * @param {Function} [props.onRowOrderChange] - called with the row ids in their new order after a drag; omit to disable dragging
 * @returns {React.ReactElement} the prompts data grid
 */
const PromptsTable = ({ prompts, isLoading, onDelete, onRowOrderChange }) => {
  const columns = useColumnConfig({ onDelete })

  const handleRowOrderChange = params => {
    if (!onRowOrderChange) {
      return
    }
    const oldIndex = params.oldIndex
    const targetIndex = params.targetIndex
    if (oldIndex === targetIndex) {
      return
    }

    const reorderedIds = prompts.map(row => row.id)
    const [movedId] = reorderedIds.splice(oldIndex, 1)
    reorderedIds.splice(targetIndex, 0, movedId)

    onRowOrderChange(reorderedIds)
  }

  return (
    <DataGrid
      columns={columns}
      commonColumnProps={{ headerAlign: 'left' }}
      rows={prompts}
      rowCount={prompts.length}
      loading={isLoading}
      name="agenticPromptsTable"
      rowReordering={!!onRowOrderChange}
      onRowOrderChange={handleRowOrderChange}
      localeText={{
        noRowsLabel: 'No prompts match your filters yet.',
      }}
      getRowHeight={() => 'auto'}
      sx={{
        '& .MuiDataGrid-cell': { py: 1 },
      }}
    />
  )
}

PromptsTable.propTypes = {
  prompts: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onRowOrderChange: PropTypes.func,
}

export default PromptsTable
